import {
  Button,
  FormSelect,
  FormTextArea,
  Input,
  Modal,
  PhoneInput,
  ToggleSwitch,
} from '@/components/modules';
import { useCheckLoginId } from '@/remote/user-management/check-login-id';
import type { NewUser } from '@/types/site';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

interface UserAddModalProps {
  isOpen: boolean;
  onAdd: (user: NewUser) => Promise<void>;
  onCancel: () => void;
}

export default function UserAddModal({
  isOpen,
  onAdd,
  onCancel,
}: Readonly<UserAddModalProps>) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkTrigger, setCheckTrigger] = useState(0);

  // React Hook Form 설정
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    reset,
    setError: setFormError,
    clearErrors,
    setValue,
  } = useForm<NewUser>({
    mode: 'onChange',
    defaultValues: {
      loginId: '',
      password: '',
      passwordConfirm: '',
      name: '',
      email: '',
      userLevel: 'TOWN_MANAGER',
      fullAddress: '',
      mobilePhone: '',
      phone: '',
      smsReceive: false,
      description: '',
    },
  });

  const watchedLoginId = watch('loginId');
  const watchedPassword = watch('password');
  const watchedPasswordConfirm = watch('passwordConfirm');

  // 로그인 ID 중복 확인
  const { data: isLoginIdTaken, isLoading: isIdCheckLoading } = useCheckLoginId(
    watchedLoginId,
    checkTrigger
  );

  // 로그인 ID가 변경되면 중복 확인 상태 리셋
  useEffect(() => {
    setCheckTrigger(0);
    clearErrors('loginId');
  }, [watchedLoginId, clearErrors]);

  // 패스워드 확인 검증
  useEffect(() => {
    if (watchedPasswordConfirm && watchedPassword !== watchedPasswordConfirm) {
      setFormError('passwordConfirm', {
        type: 'manual',
        message: '패스워드가 일치하지 않습니다.',
      });
    } else {
      clearErrors('passwordConfirm');
    }
  }, [watchedPassword, watchedPasswordConfirm, setFormError, clearErrors]);

  const onSubmit = async (data: NewUser) => {
    // 로그인 ID 중복 확인이 완료되지 않은 경우
    if (checkTrigger === 0) {
      setError('로그인 ID 중복 확인을 해주세요.');
      alert('로그인 ID 중복 확인을 해주세요.');
      return;
    }

    // 로그인 ID가 중복인 경우
    if (isLoginIdTaken) {
      setError('이미 사용 중인 로그인 ID입니다.');
      alert('이미 사용 중인 로그인 ID입니다.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const filteredData = Object.fromEntries(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Object.entries(data).filter(([_, value]) => value !== '')
      );
      await onAdd(filteredData as NewUser);
      reset(); // 폼 초기화
    } catch (error) {
      console.error('유저 추가 오류:', error);
      setError(
        error instanceof Error
          ? error.message
          : '유저 추가 중 오류가 발생했습니다.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginIdCheck = () => {
    if (watchedLoginId.length >= 3) {
      setCheckTrigger((prev) => prev + 1);
    }
  };

  const isFormValid = isValid && checkTrigger > 0 && !isLoginIdTaken;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title="새 회원 추가"
      size="4xl"
      maxHeight="max-h-[90vh]"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-start space-x-2">
                <Input
                  label="로그인 아이디"
                  placeholder="로그인에 사용할 아이디를 입력하세요"
                  maxLength={30}
                  required
                  className="flex-1"
                  register={register('loginId', {
                    required: '로그인 아이디는 필수 항목입니다.',
                    minLength: {
                      value: 3,
                      message: '로그인 아이디는 3자 이상이어야 합니다.',
                    },
                    maxLength: {
                      value: 30,
                      message: '로그인 아이디는 30자 이하여야 합니다.',
                    },
                  })}
                  error={errors.loginId?.message}
                />
                <div className="flex flex-col items-center space-x-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2 w-[50px] h-[20px]"></label>
                  <Button
                    type="button"
                    onClick={handleLoginIdCheck}
                    disabled={watchedLoginId.length < 3 || isIdCheckLoading}
                    variant="primary"
                    size="small"
                    className="whitespace-nowrap h-[42px]"
                  >
                    {isIdCheckLoading ? '확인 중...' : '중복 확인'}
                  </Button>
                </div>
              </div>
              {checkTrigger > 0 && watchedLoginId.length >= 3 && (
                <div className="text-sm mt-1">
                  {isIdCheckLoading ? (
                    <span className="text-gray-500">확인 중...</span>
                  ) : isLoginIdTaken ? (
                    <span className="text-red-500">
                      이미 사용 중인 로그인 ID입니다.
                    </span>
                  ) : (
                    <span className="text-green-500">
                      사용 가능한 로그인 ID입니다.
                    </span>
                  )}
                </div>
              )}
            </div>
            <div />
            <Input
              label="패스워드"
              type="password"
              placeholder="패스워드를 입력하세요"
              required
              register={register('password', {
                required: '패스워드는 필수 항목입니다.',
                minLength: {
                  value: 8,
                  message: '패스워드는 8자 이상이어야 합니다.',
                },
              })}
              error={errors.password?.message}
            />
            <Input
              label="패스워드 확인"
              type="password"
              placeholder="패스워드를 다시 입력하세요"
              required
              register={register('passwordConfirm', {
                required: '패스워드 확인은 필수 항목입니다.',
              })}
              error={errors.passwordConfirm?.message}
            />
            <Input
              label="이름"
              placeholder="회원 이름을 입력하세요"
              required={false}
              register={register('name', {
                required: false,
                minLength: {
                  value: 2,
                  message: '이름은 2자 이상이어야 합니다.',
                },
              })}
              error={errors.name?.message}
            />
            <div />
            <Input
              label="이메일"
              type="email"
              placeholder="이메일 주소를 입력하세요"
              required={false}
              register={register('email', {
                required: false,
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: '올바른 이메일 형식을 입력해주세요.',
                },
              })}
              error={errors.email?.message}
              isValid={!errors.email && !!watch('email')}
            />
            <FormSelect
              label="회원등급"
              value={watch('userLevel')}
              onChange={(value) =>
                setValue('userLevel', value as NewUser['userLevel'])
              }
              options={[
                { value: 'TOWN_MANAGER', label: '마을 관리자' },
                { value: 'PUBLIC_OFFICE_MANAGER', label: '관공서 관리자' },
              ]}
            />
            {/* <AddressInput
              label="주소"
              onChange={(value) => setValue('fullAddress', value)}
              className="md:col-span-2"
            /> */}
            <PhoneInput
              label="휴대폰 번호"
              type="mobile"
              required={false}
              register={register('mobilePhone', {
                required: false,
              })}
              error={errors.mobilePhone?.message}
            />
            <PhoneInput
              label="전화번호"
              type="landline"
              register={register('phone')}
            />
            <ToggleSwitch
              label="SMS 수신여부"
              value={watch('smsReceive')}
              onChange={(value) => setValue('smsReceive', value)}
            />
            <div />
            <FormTextArea
              label="설명"
              value={watch('description')}
              onChange={(value) => setValue('description', value)}
              placeholder="회원에 대한 설명을 입력하세요"
              maxLength={500}
              className="md:col-span-2"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="flex space-x-3 pt-6">
            <Button
              type="submit"
              disabled={!isFormValid || isLoading}
              variant="primary"
              size="medium"
              fullWidth
            >
              {isLoading ? '추가 중...' : '회원 추가'}
            </Button>
            <Button
              type="button"
              onClick={onCancel}
              variant="secondary"
              size="medium"
              fullWidth
            >
              취소
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
